/**
 * Taille maximale d'un audio envoyé au Coach Debrief (micro ou fichier importé), appliquée par le
 * serveur AVANT d'avoir reçu le fichier en entier (décision de Valérie du 29/09/2026) :
 *  1. la taille annoncée par le navigateur (`Content-Length`) est lue d'abord : au-delà, refus immédiat ;
 *  2. sinon, la lecture compte les octets reçus et s'arrête dès que la limite est dépassée.
 * La même limite (100 Mo) est affichée à l'écran et vérifiée dans le navigateur.
 */

export const MAX_AUDIO_UPLOAD_BYTES = 100 * 1024 * 1024;
/** Marge pour l'enveloppe multipart (champs du formulaire, séparateurs) autour du fichier. */
export const MULTIPART_OVERHEAD_BYTES = 1024 * 1024;
export const MAX_AUDIO_REQUEST_BYTES = MAX_AUDIO_UPLOAD_BYTES + MULTIPART_OVERHEAD_BYTES;

/** Code d'erreur renvoyé au navigateur, qui affiche le message traduit correspondant. */
export const AUDIO_TOO_LARGE_CODE = "audio_too_large";

/** La taille annoncée dépasse-t-elle la limite ? Absente ou illisible : on ne conclut pas (la lecture comptera). */
export function announcedSizeTooLarge(contentLength: string | null, limit = MAX_AUDIO_REQUEST_BYTES): boolean {
  if (!contentLength) return false;
  const n = Number(contentLength);
  return Number.isFinite(n) && n > limit;
}

/**
 * Lit le corps de la requête en s'arrêtant dès que `limit` octets sont dépassés.
 * Renvoie `null` si c'est trop gros (la lecture est alors annulée), sinon les octets reçus.
 */
export async function readBodyWithLimit(body: ReadableStream<Uint8Array> | null, limit = MAX_AUDIO_REQUEST_BYTES): Promise<Uint8Array<ArrayBuffer> | null> {
  if (!body) return new Uint8Array(0);
  const reader = body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > limit) {
      await reader.cancel().catch(() => {});
      return null;
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) { out.set(c, offset); offset += c.byteLength; }
  return out;
}
